import React from 'react';
import styled, { css } from 'styled-components';
import Image from 'gatsby-theme-core/src/components/image';
import Button from 'gatsby-theme-core/src/components/button/index';
import CheckIcon from 'icons/src/Check';
import CoreContent from '../../content';
import { useContentTheme } from '../../content-theme';

export const Icon = styled.span`
  flex-shrink: 0;
  height: 44px;
  width: 44px;

  @supports not (mask: url()) {
    background-image: url(${({ $icon }) => $icon});
    background-size: cover;
  }

  @supports (mask: url()) {
    background-color: ${({ theme }) => theme.primaryColor};
    ${({ $contentTheme }) => $contentTheme === 'light' && css`
      background-color: white;
    `}
    ${({ $contentTheme }) => $contentTheme === 'standout' && css`
      background-color: white;
    `}
    mask: url(${({ $icon }) => $icon}) no-repeat center;
    mask-size: contain;
  }
`;

export const Title = styled.h2`
  margin-top: 0;
  margin-bottom: 0;
`;

export const Content = styled(CoreContent).attrs({
  asSpan: true,
})`
  line-height: ${({ theme }) => theme.baseLineHeight};
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  flex-grow: 1;
  padding: 1rem;

  ${({ $variant }) => $variant === 'check' && css`
    padding: 0;
    flex-grow: unset;
  `}

  ${({ $variant }) => $variant === 'buttons' && css`
    background-color: ${({ theme }) => theme.pagebuilder?.backgroundGrey || theme.grayLightColor};
  `}

  ${({ $variant }) => $variant === 'vertical' && css`
      display: grid;
      gap: 1rem;
      flex-direction: column;
      grid-auto-rows: auto;
      @media all and (min-width: ${({ theme }) => theme.breakingpoints.md}) {
        grid-template-columns: ${({ $hasIcon }) => ($hasIcon ? '100px' : '')} 1fr;
      }
      > :is(a, span) {
        display: contents;
        @media all and (min-width: ${({ theme }) => theme.breakingpoints.md}) {
          .gatsby-image-wrapper {
            grid-row: 1 / 3;
            grid-column: 1;
          }
          .gatsby-image-wrapper + *:not(.gatsby-image-wrapper) {
            grid-column: 2;
          }
        }
      }
  `}

  ${({ $variant }) => $variant !== 'vertical' && css`
    a {
      color: inherit;
      font-weight: bold;
      font-size: 1.125rem;
    }
    > *:not(${CheckIcon}) {
      flex-grow: 1;
      display: flex;
      align-items: center;
      text-align: left;
      gap: 0.75rem;
      ${({ $contentTheme }) => $contentTheme === 'light' && css`
        color: white;
      `}
    }
  `}

  a {
    text-decoration: none;
    color: inherit;
    &:hover {
      text-decoration: underline;
    }
  }

  ${Content} {
    flex-grow: 1;
  }


  ${CheckIcon} {
    width: 1.5rem;
    color: ${({ theme }) => theme.primaryColor};
  }

`;

const ListItem = ({
  className,
  title,
  content,
  icon,
  toPage,
  link,
  variant,
  appendLinkButton = false,
  chevronRight = false,
  overrideContentTheme = null,
}) => {
  const { contentTheme: contextContentTheme } = useContentTheme();
  // this doesnt bubble down to Content:
  const contentTheme = overrideContentTheme ?? contextContentTheme; // used in pdv

  const to = toPage?.slug ?? link;
  return (
    <Item
      className={className}
      $contentTheme={contentTheme}
      $variant={variant}
      $hasIcon={Boolean(icon)}
    >
      <Button
        wrapper
        possibleSpan
        chevronRight={chevronRight}
        to={appendLinkButton ? null : to}
      >
        {variant === 'check' && (
          <CheckIcon />
        )}
        {icon?.ext === '.svg' && (
          <Icon
            className="list-item-icon" // we need this in pdv styles
            $contentTheme={contentTheme}
            $icon={icon.url}
          />
        )}
        {icon?.localFile && icon?.ext !== '.svg' && (
          <Image image={icon} />
        )}
        {title && (
          <Title>{title}</Title>
        )}
        {content && (
          <Content
            content={content}
          />
        )}
        {appendLinkButton && to && (
          <Button
            chevronRight={chevronRight}
            to={to}
            link
          >
            <span>Lees meer</span>
          </Button>
        )}
      </Button>
    </Item>
  );
};

export default styled(ListItem)``;
